import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pulse } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Post-Deploy Health Ping Badge (Sprint 60.2)
 *
 * Polls `GET /api/health/diag` every 60s and surfaces a compact status
 * pill next to the kickoff countdown. Turns invisible prod plumbing into
 * a visible trust signal — users see instantly whether Vegas / injuries
 * are fresh before locking lineups.
 *
 * Status logic:
 *   • Green  ("HEALTHY")  → all 4 fetchers OK AND cache_mongo populated
 *   • Amber  ("PARTIAL")  → cache populated but ≥1 fetcher failed OR
 *                            cache is >2h old
 *   • Red    ("STALE")    → cache_mongo empty OR ≥2 fetchers failing
 *
 * Tooltip on hover shows the per-source status and last-refresh age.
 */
const POLL_INTERVAL_MS = 60_000;
const AMBER_STALE_MS = 2 * 60 * 60 * 1000;   // 2h → amber
const RED_STALE_MS = 6 * 60 * 60 * 1000;     // 6h → red even if cache populated
// Sprint 60.29 (R2 fix) — provider staleness thresholds. Every SGO/Sleeper
// signal MUST refresh at least every PROVIDER_AMBER_MS or the badge
// downgrades. PROVIDER_RED_MS is a stronger claim: something's outright
// broken (key rotated, quota exhausted, endpoint down).
const PROVIDER_AMBER_MS = 3 * 60 * 60 * 1000;
const PROVIDER_RED_MS = 12 * 60 * 60 * 1000;
// Signals we require populated for the app to function. If any of these
// is missing its last_refresh entirely (never fetched this session), we
// flag amber. Timeframes is the single source of truth for week/season,
// so it's the harshest — miss it, and every downstream call could target
// the wrong week.
const CRITICAL_PROVIDERS = ["sgo", "timeframe", "injuries", "rankings"];

function ageMs(iso) {
  if (!iso) return Infinity;
  try {
    return Date.now() - new Date(iso).getTime();
  } catch { return Infinity; }
}

function formatAge(ms) {
  if (!isFinite(ms) || ms < 0) return "never";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function classify(diag) {
  if (!diag) return { level: "unknown", label: "…", detail: "Loading" };
  const mongoCache = diag.cache_mongo || {};
  const memCache = diag.cache_in_memory || {};
  // Prefer whichever cache reports data — some environments only populate
  // the in-memory side (single-worker prod). `?? 0` was too aggressive:
  // if mongoCache reports {status: "EMPTY..."} without vegas_games, ??
  // falls through fine, but if BOTH are undefined we should stay unknown.
  const vegasMongo = typeof mongoCache.vegas_games === "number" ? mongoCache.vegas_games : null;
  const vegasMem = typeof memCache.vegas_games === "number" ? memCache.vegas_games : null;
  const vegas = vegasMongo != null ? vegasMongo : (vegasMem != null ? vegasMem : 0);
  const lastRefresh = mongoCache.last_refresh || memCache.last_refresh;
  const age = ageMs(lastRefresh);
  const fetchers = diag.fetchers || {};
  // Sprint 60.64 — exclude passive `*_probe_only` fetchers from the
  // failure count. Sprint 60.61 kept ESPN + OddsAPI in the diag endpoint
  // as *connectivity monitors* only — SGO is the sole Vegas source now.
  // If those free probes fail (network blip / DNS / unpaid credentials)
  // they used to trip the `failed.length >= 2` → red rule and flag the
  // whole app as STALE even though real Vegas data was healthy. Filter
  // them out of the health signal; they still surface in the tooltip
  // for ops visibility.
  const isProbeOnly = (name) => name.endsWith("_probe_only");
  const failed = Object.entries(fetchers)
    .filter(([name, v]) => !v?.ok && !isProbeOnly(name))
    .map(([k]) => k);

  // Red gate: only when we actually have evidence something's broken.
  if ((vegas === 0 && (vegasMongo != null || vegasMem != null)) || failed.length >= 2) {
    return {
      level: "red",
      label: "STALE",
      detail: vegas === 0
        ? `Cache empty · ${failed.length} fetcher${failed.length === 1 ? "" : "s"} down`
        : `${failed.length} fetchers down · cache ${formatAge(age)}`,
    };
  }
  if (isFinite(age) && age > RED_STALE_MS) {
    return { level: "red", label: "STALE", detail: `Cache ${formatAge(age)} old` };
  }
  if (failed.length >= 1 || (isFinite(age) && age > AMBER_STALE_MS)) {
    return {
      level: "amber",
      label: "PARTIAL",
      detail: failed.length
        ? `${failed.join(", ")} down · cache ${formatAge(age)}`
        : `Cache ${formatAge(age)}`,
    };
  }
  return {
    level: "green",
    label: "LIVE",
    detail: `${vegas} games · updated ${formatAge(age)}`,
  };
}

function classifyProviders(prov) {
  if (!prov) return { level: "unknown", stale: [], missing: [] };
  const stale = [];
  const missing = [];
  const critMissing = [];
  Object.entries(prov).forEach(([key, val]) => {
    if (!val || typeof val !== "object") return;
    const lr = val.last_refresh;
    if (!lr) {
      missing.push(key);
      if (CRITICAL_PROVIDERS.includes(key)) critMissing.push(key);
      return;
    }
    const age = ageMs(lr);
    if (age > PROVIDER_RED_MS) stale.push({ key, age, level: "red" });
    else if (age > PROVIDER_AMBER_MS) stale.push({ key, age, level: "amber" });
  });
  const anyRed = stale.some((s) => s.level === "red");
  // Sprint 60.64 — missing timestamps alone no longer trip RED. A
  // provider with `last_refresh: null` means "not confirmed healthy
  // yet on this pod" (usually a fresh redeploy before the first
  // scheduler tick), NOT "confirmed broken". Legit outages surface
  // as stale timestamps > PROVIDER_RED_MS, which stay in the RED
  // bucket. Missing-timestamp downgrades to AMBER at worst.
  const anyAmber = stale.length > 0 || critMissing.length > 0;
  return {
    level: anyRed ? "red" : anyAmber ? "amber" : "green",
    stale, missing, critMissing,
  };
}

export default function HealthPingBadge() {
  const { user } = useAuth();
  const [diag, setDiag] = useState(null);
  const [prov, setProv] = useState(null);
  const [hover, setHover] = useState(false);
  // Sprint 60.68 — badge is a beta ops surface, not a customer feature.
  // Only admins (`user.is_admin`) see it. Every other paying user gets a
  // clean header row; when infra hiccups we absorb it silently instead
  // of scaring users with a red "STALE" pill they can't act on.
  const isAdmin = !!user?.is_admin;
  // Sprint 60.64 — track the badge's viewport rect so the hover panel
  // can render via a Portal on `document.body` and escape the sub-header
  // row's `overflow-x-auto` clipping. Previously the panel used
  // `position: absolute` inside the overflow ancestor and got hidden
  // behind the day-filter tabs — users only saw the native `title=`
  // tooltip and never the rich fetcher/provider detail.
  const anchorRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const openTooltip = () => {
    if (anchorRef.current) {
      setAnchorRect(anchorRef.current.getBoundingClientRect());
    }
    setHover(true);
  };
  const closeTooltip = () => setHover(false);

  const load = async () => {
    try {
      const [d, p] = await Promise.allSettled([
        api.healthDiag(),
        api.providersHealth(),
      ]);
      setDiag(d.status === "fulfilled" ? d.value : null);
      setProv(p.status === "fulfilled" ? p.value : null);
    } catch {
      setDiag(null);
      setProv(null);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;   // Non-admins: never poll, never render (see below)
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAdmin]);

  if (!isAdmin) return null;

  const base = classify(diag);
  const provStat = classifyProviders(prov);
  // Compose: worst of the two rules. Provider red beats fetcher amber, etc.
  const rank = { green: 0, amber: 1, red: 2, unknown: -1 };
  const composedLevel = rank[provStat.level] > rank[base.level] ? provStat.level : base.level;
  const { level, label, detail } = composedLevel === base.level
    ? base
    : {
        level: composedLevel,
        label: composedLevel === "red" ? "STALE" : "PARTIAL",
        detail: provStat.stale.length
          ? `${provStat.stale.length} provider${provStat.stale.length === 1 ? "" : "s"} stale · ${provStat.stale[0].key}`
          : `${provStat.critMissing.length || provStat.missing.length} provider(s) offline`,
      };

  const dot = {
    green: "bg-[#39FF14]",
    amber: "bg-amber-400",
    red: "bg-red-500 animate-pulse",
    unknown: "bg-zinc-600",
  }[level];

  const text = {
    green: "text-[#39FF14]",
    amber: "text-amber-400",
    red: "text-red-400",
    unknown: "text-zinc-500",
  }[level];

  return (
    <div
      ref={anchorRef}
      data-testid="health-ping-badge"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
      onFocus={openTooltip}
      onBlur={closeTooltip}
      tabIndex={0}
      className="relative inline-flex items-center gap-1.5 mr-3 cursor-help select-none"
      title="Live data feed status"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
      <span className={`overline mono text-[10px] font-bold ${text}`}>{label}</span>
      {hover && (diag || prov) && anchorRect && createPortal(
        <div
          data-testid="health-ping-tooltip"
          style={{
            position: "fixed",
            // Anchor to the badge's right edge so the panel opens
            // toward the viewport center (never off-screen right).
            top: anchorRect.bottom + 6,
            right: Math.max(8, window.innerWidth - anchorRect.right),
            zIndex: 9999,
          }}
          className="bg-black border border-zinc-700 shadow-xl px-3 py-2 min-w-[240px] max-w-[320px]"
          role="tooltip"
        >
          <div className={`text-[10px] mono uppercase tracking-widest ${text} mb-1 flex items-center gap-1`}>
            <Pulse size={10} weight="fill" />
            {label}
          </div>
          <div className="text-[10px] text-zinc-400 mono mb-2">{detail}</div>
          <div className="border-t border-zinc-800 pt-1.5 space-y-0.5">
            {Object.entries((diag && diag.fetchers) || {}).map(([name, f]) => {
              const isProbe = name.endsWith("_probe_only");
              return (
                <div key={name} className="flex items-center justify-between text-[9px] mono">
                  <span className={isProbe ? "text-zinc-600" : "text-zinc-500"}>
                    {name.replace(/_probe_only$/, "")}
                    {isProbe && <span className="ml-1 text-[8px] text-zinc-700">(probe)</span>}
                  </span>
                  <span className={
                    f?.ok
                      ? "text-zinc-300"
                      : isProbe
                        ? "text-zinc-600"  // probe fails are informational, not red
                        : "text-red-400"
                  }>
                    {f?.ok ? `${f.count ?? "—"} · ${f.elapsed_s}s` : (f?.error?.slice(0, 30) || "fail")}
                  </span>
                </div>
              );
            })}
          </div>
          {prov && (
            <div className="border-t border-zinc-800 pt-1.5 mt-1.5 space-y-0.5">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-0.5">Providers</div>
              {Object.entries(prov)
                .filter(([, v]) => v && typeof v === "object" && ("last_refresh" in v || "current" in v))
                .map(([name, v]) => {
                  const age = ageMs(v.last_refresh);
                  const cnt = v.count ?? v.projections_count ?? v.players_with_news ?? (v.current ? 1 : 0);
                  const staleLvl = !v.last_refresh
                    ? "text-zinc-600"
                    : age > PROVIDER_RED_MS
                    ? "text-red-400"
                    : age > PROVIDER_AMBER_MS
                    ? "text-amber-400"
                    : "text-zinc-300";
                  return (
                    <div key={name} className="flex items-center justify-between text-[9px] mono">
                      <span className="text-zinc-500">{name.replace(/^sdio_/, "").slice(0, 14)}</span>
                      <span className={staleLvl}>
                        {v.last_refresh ? `${cnt} · ${formatAge(age)}` : "—"}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
